import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { toneForVariant } from '../theme/adminVisualSystem';

/**
 * SectionAnalyticsHeader — compact mini analytics strip for a tab header.
 *
 * Props:
 *   title            — main page title (string)
 *   helper           — small subtitle / helper text
 *   items            — array of arbitrary records with a date field
 *   dateField        — field name to group by (default 'created_at')
 *   days             — number of day buckets (default 14)
 *   chartLabel       — caption for the sparkline (e.g. "Daily Ticket Inflow")
 *   stats            — array of { label, value, tone?, variant?, key? }
 *                      `key` is the filter token sent to onStatClick
 *   totalsFromItems  — if true, auto-include "Total" as first stat with items.length
 *   activeStatKey    — currently active stat key (for visual highlight)
 *   onStatClick      — fn(key) — when set, every tile becomes clickable.
 *                      Total tile sends key = 'all'.
 *
 * Compact SVG sparkline (no recharts) keeps the footprint minimal.
 */
export default function SectionAnalyticsHeader({
  title,
  helper,
  items = [],
  dateField = 'created_at',
  days = 14,
  chartLabel = 'Daily Inflow',
  stats = [],
  totalsFromItems = true,
  activeStatKey,
  onStatClick,
}) {
  const buckets = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const list = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      list.push({ key: d.toISOString().slice(0, 10), label: `${d.getMonth() + 1}/${d.getDate()}`, count: 0 });
    }
    for (const r of items) {
      const raw = r?.[dateField];
      if (!raw) continue;
      const key = new Date(raw).toISOString().slice(0, 10);
      const b = list.find((x) => x.key === key);
      if (b) b.count += 1;
    }
    return list;
  }, [items, dateField, days]);

  const max = Math.max(1, ...buckets.map((b) => b.count));
  const total = buckets.reduce((a, b) => a + b.count, 0);

  // Resolve stat tones (from string tone key or variant key)
  const resolvedStats = useMemo(() => {
    const first = totalsFromItems
      ? [{ label: 'Total', value: items.length, variant: 'primary', key: 'all' }]
      : [];
    return [...first, ...stats].map((s) => ({
      ...s,
      _tone: s.tone || toneForVariant(s.variant || 'secondary'),
    }));
  }, [stats, totalsFromItems, items.length]);

  const W = 280;
  const H = 48;
  const pad = 2;

  // Build line + area path
  const pts = buckets.map((b, i) => {
    const x = pad + (i * (W - pad * 2)) / Math.max(1, buckets.length - 1);
    const y = H - pad - ((b.count / max) * (H - pad * 2));
    return [x, y];
  });
  const linePath = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${pts.at(-1)[0]} ${H - pad} L ${pts[0][0]} ${H - pad} Z`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="border border-[#1a1a1a] bg-[#0d0d0d] p-4 sm:p-5"
      data-testid="section-analytics-header"
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
        {/* Left — title */}
        <div className="lg:min-w-[240px]">
          <h1 className="font-outfit text-2xl font-semibold text-white tracking-tight">{title}</h1>
          {helper && (
            <p className="text-xs font-mono text-[#888] mt-1">{helper}</p>
          )}
        </div>

        {/* Middle — sparkline */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] font-mono text-[#666] tracking-[0.2em] uppercase">
              {chartLabel} · {days}D
            </p>
            <p className="text-[10px] font-mono text-[#555] tabular-nums">
              {total} events
            </p>
          </div>
          <div className="relative w-full overflow-hidden">
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" className="block">
              <defs>
                <linearGradient id="section-area" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#90E0EF" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="#90E0EF" stopOpacity="0" />
                </linearGradient>
              </defs>
              {/* baseline */}
              <line x1="0" y1={H - pad} x2={W} y2={H - pad} stroke="#1a1a1a" strokeWidth="0.5" />
              {total === 0 ? (
                <text x={W / 2} y={H / 2 + 3} textAnchor="middle" fill="#444" fontSize="9" fontFamily="JetBrains Mono, monospace">
                  // no activity yet
                </text>
              ) : (
                <>
                  <motion.path
                    d={areaPath}
                    fill="url(#section-area)"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6 }}
                  />
                  <motion.path
                    d={linePath}
                    fill="none"
                    stroke="#90E0EF"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.9, ease: 'easeOut' }}
                  />
                  {/* Last point marker */}
                  <circle cx={pts.at(-1)[0]} cy={pts.at(-1)[1]} r="2.2" fill="#90E0EF" />
                </>
              )}
            </svg>
          </div>
        </div>

        {/* Right — stat pills (optionally clickable as filter shortcuts) */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 lg:grid-cols-none lg:flex lg:flex-wrap lg:max-w-[420px]">
          {resolvedStats.map((s, i) => {
            const clickable = !!onStatClick && s.key !== undefined;
            const active = clickable && (activeStatKey ?? 'all') === s.key;
            const baseStyle = {
              borderColor: active ? (s._tone?.text || s._tone?.border || '#90E0EF') : (s._tone?.border || undefined),
              background: active ? (s._tone?.bg || 'rgba(144,224,239,0.08)') : undefined,
              boxShadow: active ? `0 0 10px ${s._tone?.glow || 'rgba(144,224,239,0.30)'}` : undefined,
            };
            const Wrapper = clickable ? motion.button : motion.div;
            const wrapperProps = clickable
              ? {
                  type: 'button',
                  onClick: () => onStatClick(s.key),
                  'data-clickable': 'true',
                  'data-testid': `analytics-stat-${s.key}`,
                  whileHover: { y: -1.5 },
                  whileTap: { scale: 0.97 },
                  style: { ...baseStyle, cursor: 'pointer' },
                }
              : { style: baseStyle };
            return (
              <Wrapper
                key={s.label + i}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.08 * i }}
                className="border border-[#1a1a1a] bg-[#0a0a0a] px-2.5 py-1.5 min-w-[72px] text-left rounded-none transition-colors"
                {...wrapperProps}
              >
                <p className="text-[9px] font-mono text-[#666] tracking-[0.18em] uppercase">
                  {s.label}
                </p>
                <p
                  className="font-outfit text-lg font-semibold tabular-nums leading-tight"
                  style={{ color: s._tone?.text || '#ffffff' }}
                >
                  {s.value ?? 0}
                </p>
              </Wrapper>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}
